import { HomeTodoCategory } from "@coaching-culture/types";
import {
  Alert,
  Box,
  Button,
  Flex,
  Label,
  Panel,
  PanelHeader,
  Text,
  typeSize,
} from "@coaching-culture/ui";
import React from "react";
import { FaExclamationTriangle } from "react-icons/fa";
import styled, { useTheme } from "styled-components";

// Page-level layout only. Cards, buttons, labels and the rest come from
// @coaching-culture/ui so the page follows the theme (and org branding).

export const Layout = styled.div`
  max-width: 1400px;
  margin: 0;
  /* Extra top space clears the app's floating sidebar toggle on phones. */
  padding: 44px 16px 48px;
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(0, 1fr);
  grid-template-areas:
    "header"
    "next"
    "todo"
    "goals"
    "learning";

  @media (min-width: 1024px) {
    padding: 32px 36px 48px;
    gap: 24px;
    grid-template-columns: minmax(0, 1.6fr) minmax(320px, 1fr);
    grid-template-areas:
      "header header"
      "todo next"
      "todo goals"
      "todo learning"
      "todo .";
    align-items: start;
  }
`;

export const Area = styled.div<{ area: string }>`
  grid-area: ${(p) => p.area};
  min-width: 0;
`;

/** A to-do category label. */
export function CategoryLabel({
  category,
  children,
}: {
  category: HomeTodoCategory;
  children: React.ReactNode;
}) {
  const theme = useTheme();

  return (
    <Label
      size="small"
      color={theme.colors[`${category}Soft`]}
      textColor={theme.colors[`${category}Text`]}
    >
      {children}
    </Label>
  );
}

export type HomeCardProps = {
  title: React.ReactNode;
  titleId: string;
  /** Shown at the right of the header, e.g. a "View all" link. */
  action?: React.ReactNode;
  busy?: boolean;
  /** A line under the header. Off when tabs sit directly beneath it. */
  headerRule?: boolean;
  children: React.ReactNode;
};

/** A titled card: the shared Panel with a PanelHeader. */
export function HomeCard({
  title,
  titleId,
  action,
  busy,
  headerRule = true,
  children,
}: HomeCardProps) {
  return (
    <Panel
      as="section"
      aria-labelledby={titleId}
      aria-busy={busy || undefined}
      overflow="hidden"
    >
      <PanelHeader
        justifyContent="space-between"
        style={{ gap: 12, ...(headerRule ? null : { borderBottom: 0 }) }}
      >
        <Text as="h2" id={titleId} m={0} fontSize="title" fontWeight={600}>
          {title}
        </Text>
        {action}
      </PanelHeader>
      {children}
    </Panel>
  );
}

/** Padding for a card's content. */
export const CardBody = styled(Box)``;
CardBody.defaultProps = { p: 3 };

/** A failed load with a retry button. */
export function LoadError({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Box p={3} role="alert">
      <Alert color="danger" icon={FaExclamationTriangle}>
        <Flex
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          style={{ gap: 12 }}
        >
          <span>{message}</span>
          <Button type="button" small filled color="danger" onClick={onRetry}>
            Try again
          </Button>
        </Flex>
      </Alert>
    </Box>
  );
}

/** Secondary text. "body" is the darkest theme grey that still reads as muted. */
export const Muted = styled(Text).attrs({ color: "body" })``;

/** Small all-caps label, in the theme's primary colour. */
export const Eyebrow = styled(Text)`
  margin: 0;
  font-size: ${typeSize("caption")};
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.primary};
`;

/** Small all-caps heading for a section inside a card. */
export const SectionTitle = styled(Text).attrs({ as: "h3" })`
  margin: 0 0 8px;
  font-size: ${typeSize("caption")};
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(p) => p.theme.colors.body};
`;

/** A list of rows separated by thin rules. */
export const RuleList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  & > li + li {
    border-top: 1px solid ${(p) => p.theme.colors.grey4};
  }
`;
